import React, { useEffect, useMemo, useRef, useState } from "react";
import { LoaderComponent } from "../../../../../shared/components/loaders/page-loader";
import { PageWrapper } from "../../../transaction-certificates/tc-dashboard/cb-landingpage";
import { Box, Divider, Grid, IconButton, Paper, Tooltip, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button, Alert, CircularProgress } from "@mui/material";
import FilterListIcon from '@mui/icons-material/FilterList';
import { StyledButton } from "../../../../../shared/components/buttons/custom-button";
import { CustomSelect } from "../../../../../shared/components/inputfields/form-elements";
import FormBuilderV4 from "../../../../../shared/components/form-builder/form-builder-v4";
import * as Yup from "yup";
import CustomTable from "../../../../../shared/components/table/custom-table";
import { MRT_ColumnDef } from "material-react-table";
import { useNavigate } from "react-router-dom";
import { useApi } from "../../../../../shared/hooks/useApi";
import { API_ROUTES } from "../../../../../shared/app-configs/sc-app-config";
import { OptionType } from "../../../../../shared/components/form-builder/form-config.model";
import { useTransactionCertificateStore } from "../../../../../store/transaction-certificates.store";
import { useFormActions } from "../../../../../shared/hooks/form-builder.hooks";
import { StatusCell } from "../../../transaction-certificates/tc-dashboard/cbLandingPageTable.config";
import { RejectReasonCell } from "./scTableConfig";
import { coNameDetailsService } from "../../../transaction-certificates/tc-service/buyer-seller-details.service";
import { Utills } from "../../../../../shared/app-configs/utilit";
import moment from "moment";
import { useScStore } from "../../../../../store/scStandard.store";
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RateReviewIcon from '@mui/icons-material/RateReview';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import Chip from '@mui/material/Chip';
import ViewerModalPopup from "../../../../../shared/components/modals/viewerModal";
import PdfViewer from "../../../../../shared/components/preview/pdf-viewer";
import useNotification from "../../../../../shared/hooks/useNotification";
import { StringValidation } from "../../../../../shared/utils/custom-validation.utils";
import { dtrackItServiceV4 } from "../../sc-service/dtrackit.service";
import { scCacheService } from "../utils/scCacheService";
import { ScPdfUploaderModal } from "./ScPdfUploaderModal";
import { downloadPDF } from "../../../../../shared/hooks/file-downloader";

interface NavTCNoProps {
  cell: {
    getValue: () => string;
    row: {
      original: {
        submissionStatus: string;
        scId: string;
        parentSubmissionStatus: string;
        scNo?: string;
        scVersionNo?: string;
        [key: string]: any;
      };
    };
  },
  type: string;
}
const NavTCNo: React.FC<NavTCNoProps> = ({ cell }) => {
  const navigate = useNavigate();

  // Extracting necessary values
  const tcNo = cell.getValue(); // Transaction Certificate Number
  const { submissionStatus, scId } = cell.row.original;

  // Click handler to navigate based on submissionStatus
  const handleNavigation = () => {
    if (submissionStatus === "Submitted" || submissionStatus === "Approved") {
      navigate(`/v4/sc/submited-sc/scope-certificate-details/${scId}`);
    }
    else if (submissionStatus === "Rejected") {
      navigate(`/v4/sc/rejected-sc/scope-certificate-details/${scId}`);
    }
    else {
      navigate(`/v4/sc/sc-details/scope-certificate-details/${scId}`);
    }
  };

  return (
    <Typography
      onClick={handleNavigation}
      variant="body2"
      sx={{
        cursor: "pointer",
        color: "primary.main",
        textDecoration: "underline",
        "&:hover": {
          color: "primary.dark", // Slightly darker color on hover
        },
        "&:active": {
          textDecoration: "none", // Remove underline when clicked
        },
      }}
    >
      {tcNo}
    </Typography>
  );
};
const SuppliersDashboard = () => {
  const { executeGet, setLoadingState, loading } = useApi();
  const formRef = useRef<any>(null);
  const navigate = useNavigate();
  const [singleValue, setSingleValue] = React.useState("dTrackit");
  const links = [{ label: "323", value: "2332" }];
  const { setNewScRequested, cbLicensingCode } = useTransactionCertificateStore();
  const { resetAllDatas } = useFormActions("")
  const [isResetEnabled, setResetEnable] = useState(false);
  const [isSearchDisabled, setSearchDisabled] = useState(true);
  const { setEditSCInfo: updateScInfo } = useScStore();
  const isCbUser = cbLicensingCode.startsWith("CB");
  const { notify } = useNotification();

  const getStandardDisplayInfo = (val: string) => {
    const cleanVal = val.trim();
    if (!cleanVal) return { code: "", label: "" };

    // Check standardDetails first
    const matchDetail = standardDetails.find(
      det =>
        String(det.value).toLowerCase() === cleanVal.toLowerCase() ||
        String(det.code).toLowerCase() === cleanVal.toLowerCase() ||
        String(det.label).toLowerCase().includes(cleanVal.toLowerCase())
    );

    if (matchDetail) {
      return {
        code: matchDetail.code || matchDetail.value || cleanVal,
        label: matchDetail.label,
      };
    }

    // Try extracting code in parenthesis, e.g. "Organic Content Standard (OCS)"
    const parenMatch = cleanVal.match(/\(([^)]+)\)/);
    if (parenMatch) {
      return {
        code: parenMatch[1],
        label: cleanVal,
      };
    }

    // If it's a long description but has no parentheses, check if we can shorten it
    if (cleanVal.length > 8) {
      const initials = cleanVal
        .split(" ")
        .map(w => w[0])
        .join("")
        .toUpperCase();
      return {
        code: initials,
        label: cleanVal,
      };
    }

    return {
      code: cleanVal,
      label: cleanVal,
    };
  };

  const onNewRequestTCClick = () => {
    // Clear ALL L1 Zustand cache + L2 localStorage so a new SC session starts fresh.
    // Without this, standards/process-code warning banners from a previous SC bleed in.
    const {
      clearScData,
      clearFacilityListCache,
      clearFacilityCache,
      clearReviewedFacilities,
      setScStandards,
    } = useScStore.getState();
    clearScData();
    clearFacilityListCache();
    clearFacilityCache();
    clearReviewedFacilities();
    setScStandards([]);
    // L2 localStorage — no SC_ID yet for a new SC, so nothing to clear from L2.
    // Navigate to request SC page
    updateScInfo({});
    setNewScRequested(true);
    navigate("/v4/sc/create-sc/scope-certificate-details");
  };
  const [dTrackitdata, setDTrackitdata] = useState<Array<any>>([]);
  const [historicdata, setHistoricdata] = useState<Array<any>>([]);
  const [standardDetails, setStandardDetails] = useState<OptionType[]>([]);
  const [rowCount, setRowCount] = useState<number>(0); // Row count for pagination
  const [pagination, setPagination] = useState(
    { pageIndex: 0, pageSize: 25 }
  );
  const fetchDtraickitDetails = async (params?: any) => {
    try {
      const queryParams = {
        scCbLicensingCode: cbLicensingCode,
        limit: pagination.pageSize,
        offset: pagination.pageIndex * pagination.pageSize,
        ...params
      };

      // Clean up parameters - remove null/undefined values
      Object.keys(queryParams).forEach(key => {
        if (queryParams[key] === null || queryParams[key] === undefined || queryParams[key] === '') {
          delete queryParams[key];
        }
      });

      // Handle co_name parameter - ensure it's properly formatted
      if (params?.co_name && Array.isArray(params.co_name)) {
        queryParams.co_name = params.co_name.filter((name: string) => name && name.trim() !== '');
      }

      console.log('Fetching dTrackit details with params:', queryParams);

      const result = await executeGet(API_ROUTES.GET_SC_DASHBOARD_V4, queryParams);

      console.log('API Response:', result?.data);

      if (result?.data) {
        const responseData = result.data.response || [];
        const recordCount = result.data.no_of_records || 0;

        const mapped = responseData.map((item: any) => ({
          ...item,
        }));

        setDTrackitdata(mapped);
        setRowCount(recordCount);

        // Show user feedback if no results found
        if (responseData.length === 0 && recordCount === 0) {
          notify("No records found matching your search criteria", "info");
        } else if (responseData.length === 0 && recordCount > 0) {
          notify("Records exist but couldn't be loaded. Please try adjusting your search criteria.", "warning");
        }
      } else {
        console.error("Result is undefined or missing required data.");
        setDTrackitdata([]);
        setRowCount(0);
        notify("Failed to fetch data. Please try again.", "error");
      }
    } catch (error) {
      console.error("Error fetching dTrackit details:", error);
      setDTrackitdata([]);
      setRowCount(0);
      notify("Error occurred while fetching data. Please try again.", "error");
    }
  };

  const fetchHistoricalDetails = async (params?: any) => {
    try {
      const queryParams = {
        scCbLicensingCode: cbLicensingCode,
        limit: pagination.pageSize,
        offset: pagination.pageIndex * pagination.pageSize,
        ...params
      };

      // Clean up parameters - remove null/undefined values
      Object.keys(queryParams).forEach(key => {
        if (queryParams[key] === null || queryParams[key] === undefined || queryParams[key] === '') {
          delete queryParams[key];
        }
      });

      // Handle co_name parameter - ensure it's properly formatted
      if (params?.co_name && Array.isArray(params.co_name)) {
        queryParams.co_name = params.co_name.filter((name: string) => name && name.trim() !== '');
      }

      console.log('Fetching historical details with params:', queryParams);

      const result = await executeGet(API_ROUTES.GET_SC_HISTORICAL_DASHBOARD_V4, queryParams);

      console.log('Historical API Response:', result?.data);

      if (result?.data) {
        const responseData = result.data.response || [];
        const recordCount = result.data.no_of_records || 0;

        setHistoricdata(responseData);
        setRowCount(recordCount);

        // Show user feedback if no results found
        if (responseData.length === 0 && recordCount === 0) {
          notify("No records found matching your search criteria", "info");
        } else if (responseData.length === 0 && recordCount > 0) {
          notify("Records exist but couldn't be loaded. Please try adjusting your search criteria.", "warning");
        }
      } else {
        console.error("Result is undefined or missing required data.");
        setHistoricdata([]);
        setRowCount(0);
        notify("Failed to fetch data. Please try again.", "error");
      }
    } catch (error) {
      console.error("Error fetching historical details:", error);
      setHistoricdata([]);
      setRowCount(0);
      notify("Error occurred while fetching data. Please try again.", "error");
    }
  };

  const handleUploadSuccess = (targetScId: string, uploadResponse: any) => {
    const uploadedFiles = uploadResponse?.file_upload?.uploaded_files || [];
    if (uploadedFiles.length === 0) return;

    const getBaseScNo = (scNum: string) => {
      if (!scNum) return "";
      const scNumUpper = scNum.trim().toUpperCase();
      const match = scNumUpper.match(/-(\d{2})$/);
      if (match) {
        return scNumUpper.replace(/\d{2}$/, "00");
      }
      return scNumUpper;
    };

    const updateRowsList = (prevData: Array<any>) => {
      const targetRow = prevData.find(row => row.scId === targetScId);
      const targetBaseScNo = targetRow ? getBaseScNo(targetRow.scNo) : "";

      return prevData.map(row => {
        const isTargetRow = row.scId === targetScId;
        const matchesTargetBase = targetBaseScNo && getBaseScNo(row.scNo) === targetBaseScNo;

        if (isTargetRow || matchesTargetBase) {
          const updatedRow = { ...row };
          let existingFiles = Array.isArray(updatedRow.scUploadedFiles) ? [...updatedRow.scUploadedFiles] : [];

          uploadedFiles.forEach((file: any) => {
            const safeFilename = file.filename;
            const isBase = file.type === "base" || safeFilename.toLowerCase().replace(".pdf", "").endsWith("-00");
            const appliesToThisRow = isBase ? matchesTargetBase : isTargetRow;

            if (appliesToThisRow) {
              const norm = (s: string) => s.toLowerCase().replace(/[-_\s]/g, "");
              const normSafe = norm(safeFilename);
              const normScNo = norm(updatedRow.scNo || "");
              const normScVerNo = norm(updatedRow.scVersionNo || "");
              const uploadedAt = new Date().toISOString();
              const newEntry = { fileName: safeFilename, uploadedAt };

              existingFiles = existingFiles.filter((f: any) => {
                const fname = typeof f === 'string' ? f : (f.fileName || "");
                const normFname = norm(fname);
                if (normFname === normSafe) return false;

                if (isBase) {
                  if (normFname.includes(normScNo) && (normFname.endsWith("00pdf") || !normFname.includes(normScVerNo))) {
                    return false;
                  }
                } else {
                  if (normFname.includes(normScVerNo) && !normFname.endsWith("00pdf")) {
                    return false;
                  }
                }
                return true;
              });

              existingFiles.unshift(newEntry);

              if (isBase) {
                updatedRow.scNoPdfUrl = file.blob_url;
              } else {
                updatedRow.scVersionNoPdfUrl = file.blob_url;
                updatedRow.scVersionPdfUrl = file.blob_url;
              }

              if (updatedRow.scVersionNoPdfUrl) {
                updatedRow.scDocumentUrl = updatedRow.scVersionNoPdfUrl;
                updatedRow.scDocumentName = safeFilename;
              } else {
                updatedRow.scDocumentUrl = updatedRow.scNoPdfUrl || file.blob_url;
                updatedRow.scDocumentName = safeFilename;
              }

              updatedRow.scPdfUploadStatus = "Uploaded";
            }
          });

          updatedRow.scUploadedFiles = existingFiles;
          return updatedRow;
        }
        return row;
      });
    };

    if (singleValue === 'dTrackit') {
      setDTrackitdata(prev => updateRowsList(prev));
    } else {
      setHistoricdata(prev => updateRowsList(prev));
    }
  };

  // Fetch SC standard values from DTrackIt API
  const fetchStandardDetails = async () => {
    try {
      const response = await dtrackItServiceV4.getScStandardValues();
      if (response?.data) {
        const transformedOptions = response.data.map((item: any) => ({
          label: `${item.description_1} (${item.code_1})`,
          value: item.code_1,
          code: item.code_1
        }));
        setStandardDetails(transformedOptions);
      }
    } catch (error) {
      console.error('Error fetching SC standard values:', error);
      notify('Failed to load SC standard options', 'error');
    }
  };

  useEffect(() => {
    if (singleValue === 'dTrackit')
      fetchDtraickitDetails();
    else
      fetchHistoricalDetails()
  }, [pagination.pageIndex, pagination.pageSize, singleValue]);

  useEffect(() => {
    resetAllDatas();
    fetchStandardDetails(); // Fetch standard details on component mount
    // Ensure SC standards are cleared when landing on the dashboard
    const { setScStandards } = useScStore.getState();
    setScStandards([]);
  }, []);
  const sourceOptions: OptionType[] = [
    { label: "SC (dTrackit)", value: "dTrackit" },
    { label: "SC (historical)", value: "CB-ITS" },
  ];

  const EditSC: React.FC<NavTCNoProps> = ({ cell, type }) => {
    const { scId, submissionStatus, parentSubmissionStatus, scStatus } = cell.row.original;
    const navigate = useNavigate();
    const { setEditSCInfo } = useScStore();
    const { executePost } = useApi();
    const [previewOpen, setPreviewOpen] = useState(false);
    const [activeTab, setActiveTab] = useState<'version' | 'base'>('version');
    const fileInputRef = useRef<HTMLInputElement>(null);

    const togglePreviewDrawer = () => setPreviewOpen((prev) => !prev);

    // Click handler to navigate based on tcMetadataStatus
    const handleNavigation = () => {
      if (type === 'suppliers') {
        navigate(`/sc/submited-sc/scope-certificate-details/${scId}`);
      }
      else {
        // Clear stale L1 + L2 cache for the target SC so warnings are always fresh.
        const {
          clearScData,
          clearFacilityListCache,
          clearFacilityCache,
          clearReviewedFacilities,
          setScStandards,
        } = useScStore.getState();
        clearScData();
        clearFacilityListCache();
        clearFacilityCache();
        clearReviewedFacilities();
        setScStandards([]);
        scCacheService.clearAll(scId);

        if (
          parentSubmissionStatus === "Submitted" ||
          parentSubmissionStatus === "Success" ||
          parentSubmissionStatus === "Rejected" ||
          submissionStatus === "Amending"
        ) {
          setEditSCInfo({ scId: scId, isParentStatus: true });
        }
        else {
          setEditSCInfo({ scId: scId, isParentStatus: false });
        }
        navigate(`/v4/sc/sc-details/scope-certificate-details/${scId}`);
      }
    }
    const handleEdit = async (row: any) => {
      try {
        // ── Step 1: Re-fetch the live SC record to get the current scId ──────────
        // The component may hold a stale scId if the SC was previously amended.
        // Always resolve the latest scId via scNo before calling the edit endpoint.
        const scNo = row.scNo;
        const freshListResult: any = await executeGet(API_ROUTES.GET_SC_DASHBOARD_V4, {
          scCbLicensingCode: cbLicensingCode,
          scNo: scNo,
          limit: 1,
          offset: 0,
        });

        const freshRecords = freshListResult?.data?.response;
        if (!freshRecords || freshRecords.length === 0) {
          notify("This Scope Certificate is no longer available.", "error");
          return;
        }

        const freshDoc   = freshRecords[0];         // Full live document from Cosmos DB
        const freshScId  = freshDoc.scId;           // Current active scId

        // ── Step 2: Call edit-sc-v4 with the fresh scId and live document ─────────
        const payload = {
          ...freshDoc,
          scCbLicensingCode: cbLicensingCode,
        };

        const url = `${API_ROUTES.EDIT_SC_DETAILS_V4}?` +
          `scId=${freshScId}&` +
          `scCbLicensingCode=${encodeURIComponent(cbLicensingCode)}`;

        const result: any = await executePost(url, payload);

        if (result.status === 200) {
          // Clear stale L1 + L2 cache for the new/copied SC so warnings are always fresh.
          const {
            clearScData,
            clearFacilityListCache,
            clearFacilityCache,
            clearReviewedFacilities,
            setScStandards,
          } = useScStore.getState();
          clearScData();
          clearFacilityListCache();
          clearFacilityCache();
          clearReviewedFacilities();
          setScStandards([]);
          scCacheService.clearAll(result.data.scId);
          scCacheService.clearAll(freshScId); // also clear source SC cache

          if (
            freshDoc.submissionStatus === "Submitted" ||
            freshDoc.submissionStatus === "Success" ||
            freshDoc.submissionStatus === "Rejected"
          ) {
            setEditSCInfo({
              scId: result.data.scId,
              isParentStatus: true
            });
          }
          else {
            setEditSCInfo({});
          }
          navigate(`/v4/sc/sc-details/scope-certificate-details/${result.data.scId}`);
        } else {
          const failMessage = result?.data?.Failed || "Failed to edit SC. Please try again.";
          notify(failMessage, "error");
        }
      }
      catch (e) {
        console.error(`Exception Occured in Editing SC ${e}`)
        notify("An unexpected error occurred while editing the SC.", "error");
      }
    }

    const handleView = async (row: any) => {
      try {
        setPreviewOpen(true);
      }
      catch (e) {
        console.error(`Exception Occured in Viewing SC ${e}`)
      }
    }
    const [uploadModalOpen, setUploadModalOpen] = useState(false);

    const scNo = cell.row.original.scNo || '';
    const scVersionNo = cell.row.original.scVersionNo || '';

    const handleUploadPdf = () => {
      setUploadModalOpen(true);
    };

    return (
      <>
        <Box display="flex" alignItems="center">
          {["Success", "Amending"].includes(submissionStatus) && (
            <>
              <Tooltip title="Upload PDF">
                <IconButton onClick={handleUploadPdf} color="primary">
                  <UploadFileIcon sx={{ fontSize: 20, color: '#2C4748' }} />
                </IconButton>
              </Tooltip>
              <Divider flexItem sx={{ marginX: 1 }} />
            </>
          )}
          {["Success", "Rejected"].includes(submissionStatus) && !["E", "W"].includes(scStatus) && (
            <>
              <Tooltip title="Edit">
                <IconButton onClick={() => handleEdit(cell.row.original)} color="primary">
                  <EditIcon sx={{ fontSize: 20, color: '#353C52' }} />
                </IconButton>
              </Tooltip>
              <Divider flexItem sx={{ marginX: 1 }} />
            </>
          )}
          {["Amending", "Draft"].includes(submissionStatus) && isCbUser && (
            <>
              <Tooltip title="Continue Submission">
                <IconButton onClick={() => handleNavigation()} color="primary">
                  <RateReviewIcon sx={{ fontSize: 20, color: '#2C4748' }} />
                </IconButton>
              </Tooltip>
              <Divider flexItem sx={{ marginX: 1 }} />
            </>
          )}
          {submissionStatus !== "Draft" && (
            <Tooltip title="View">
              <IconButton onClick={() => handleView(cell.row.original)} color="primary">
                <VisibilityIcon sx={{ fontSize: 20, color: '#F0CD7F' }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <ScPdfUploaderModal
          open={uploadModalOpen}
          onClose={() => setUploadModalOpen(false)}
          scId={scId}
          scNo={scNo}
          scVersionNo={scVersionNo}
          scNoPdfUrl={cell.row.original.scNoPdfUrl}
          scVersionNoPdfUrl={cell.row.original.scVersionNoPdfUrl || cell.row.original.scVersionPdfUrl}
          submissionStatus={submissionStatus}
          scUploadedFiles={cell.row.original.scUploadedFiles}
          cbLicensingCode={cbLicensingCode}
          onUploadSuccess={(data) => handleUploadSuccess(scId, data)}
          scLastUpdatedDate={cell.row.original.scLastUpdatedDate}
          scCreationDate={cell.row.original.scCreationDate}
        />
        {scId && (
          <ViewerModalPopup
            open={previewOpen}
            onClose={togglePreviewDrawer}
            title="Preview"
            onDownload={() => {
              downloadPDF(
                API_ROUTES.GET_PREVIEW_SC_V4,
                { scId: scId },
                scNo ? `${scNo}.pdf` : "scope_certificate.pdf"
              );
            }}
          >
            <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
              <div style={{ flexGrow: 1, width: '100%', height: '80vh' }}>
                <PdfViewer
                  apiUrl={API_ROUTES.GET_PREVIEW_SC_V4}
                  params={{ scId: scId }}
                  fileName={scNo}
                />
              </div>
            </div>
          </ViewerModalPopup>
        )}
      </>
    );
  };

  const UploadedFileLink = ({ cell, row, fileName }: { cell: any; row: any; fileName: string }) => {
    // Helper to normalize strings by removing hyphens, underscores, and spaces
    const normalize = (str: string) => (str || "").toLowerCase().replace(/[-_\s]/g, "");

    const normFileName = normalize(fileName);
    const normScNo = normalize(row.original.scNo);
    const normScVersionNo = normalize(row.original.scVersionNo);

    // Exact matches on normalized values
    const matchesVersion = normScVersionNo && normFileName.includes(normScVersionNo);
    const matchesBase = normScNo && normFileName.includes(normScNo);

    // Determine classification
    const isBasePdf = matchesBase && (!matchesVersion || normScVersionNo === normScNo);

    return (
      <Tooltip title={fileName} arrow placement="top">
        <Chip
          label={isBasePdf ? "SC No PDF" : "SC Version No PDF"}
          variant="outlined"
          size="small"
          sx={{
            cursor: "default",
            fontWeight: 600,
            fontSize: "11px",
            borderRadius: "6px",
            borderColor: isBasePdf ? "rgba(45, 106, 79, 0.35)" : "rgba(249, 115, 22, 0.35)",
            backgroundColor: isBasePdf ? "rgba(45, 106, 79, 0.04)" : "rgba(249, 115, 22, 0.04)",
            color: isBasePdf ? "#2d6a4f" : "#d97706",
          }}
        />
      </Tooltip>
    );
  };

  // Hardcoded values replaced with API call - see fetchStandardDetails function above

  const dtrackitcolumns = useMemo<MRT_ColumnDef<any>[]>(() => [
    {
      accessorKey: "Action",
      header: "",
      minSize: 80,
      maxSize: 1000,
      size: 110,
      Cell: (props: any) => <EditSC {...props} type="dTrackit" />,
      enableColumnFilter: false,
    },
    {
      accessorKey: "scId",
      header: "id",
      size: 0, // Force size to 0
      enableHiding: true, // Allow programmatic hiding
      muiTableHeadCellProps: {
        style: { display: "none" }, // Hide the header cell
      },
      muiTableBodyCellProps: {
        style: { display: "none" }, // Hide the body cell
      },
      enableColumnFilter: false,
    },
    {
      accessorKey: "scNo",
      header: "SC No.",
      minSize: 60,
      maxSize: 1000,
      Cell: ({ cell, row }: any) => {
        const scNo = cell.getValue();
        const submittedFromUi = row.original.submittedFromUi;
        const isApi = submittedFromUi === "No" || submittedFromUi === "no" || submittedFromUi === false;
        const isUi = submittedFromUi === "Yes" || submittedFromUi === "yes" || submittedFromUi === true || submittedFromUi === "";
        const hasSource = isApi || isUi;

        return (
          <Box display="flex" alignItems="center" gap={1}>
            <span>{scNo}</span>
            {hasSource && (
              <Chip
                label={isApi ? "Api" : "UI"}
                size="small"
                sx={{
                  height: '18px',
                  fontSize: '10px',
                  fontWeight: 'bold',
                  borderColor: isApi ? '#ed6c02' : '#2e7d32',
                  color: isApi ? '#ed6c02' : '#2e7d32',
                  backgroundColor: isApi ? 'rgba(237, 108, 2, 0.05)' : 'rgba(46, 125, 50, 0.05)',
                  border: `1px solid ${isApi ? 'rgba(237, 108, 2, 0.3)' : 'rgba(46, 125, 50, 0.3)'}`,
                  borderRadius: '4px',
                  cursor: 'default',
                  '& .MuiChip-label': {
                    paddingLeft: '4px',
                    paddingRight: '4px',
                  }
                }}
              />
            )}
          </Box>
        );
      }
    },
    {
      accessorKey: "scVersionNo",
      header: "SC Version No.",
      minSize: 60,
      maxSize: 1000,
    },

    {
      accessorKey: "certifiedOrganizationName",
      header: "Certified Organization Name",
      minSize: 60,
      maxSize: 1000,
    },
    {
      id: "scStandard",
      accessorFn: (row) => Array.isArray(row.scStandard) ? row.scStandard.join(", ") : (row.scStandard || ""),
      header: "SC Standard",
      minSize: 60,
      maxSize: 1000,
      filterVariant: "select",
      filterSelectOptions: standardDetails.map(det => ({ text: det.label, value: det.value })),
      filterFn: (row, id, filterValue) => {
        const val = row.getValue(id);
        if (!filterValue) return true;
        return String(val).toLowerCase().includes(String(filterValue).toLowerCase());
      },
      Cell: ({ cell }: any) => {
        const value = cell.getValue();
        const selectedValues = typeof value === 'string' ? value.split(", ") : (Array.isArray(value) ? value : []);
        if (!selectedValues || selectedValues.length === 0 || (selectedValues.length === 1 && !selectedValues[0])) {
          return null;
        }
        return (
          <Box display="flex" flexWrap="wrap" gap="4px">
            {selectedValues.map((val: any) => {
              const { code, label } = getStandardDisplayInfo(val);
              if (!code) return null;
              return (
                <Tooltip title={label} arrow key={code}>
                  <Chip
                    label={code}
                    size="small"
                    sx={{
                      backgroundColor: 'rgba(44, 71, 72, 0.08)',
                      color: '#2C4748',
                      fontWeight: 'bold',
                      border: '1px solid rgba(44, 71, 72, 0.2)',
                      borderRadius: '6px',
                      fontSize: '11px',
                      height: '22px',
                      textTransform: 'uppercase',
                      cursor: 'help',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: '#2C4748',
                        color: '#FFFFFF',
                        borderColor: '#2C4748',
                      }
                    }}
                  />
                </Tooltip>
              );
            })}
          </Box>
        );
      },
    },
    {
      accessorKey: "scStatus",
      header: "SC Status",
      minSize: 60,
      maxSize: 1000,
      filterVariant: "select",
      filterSelectOptions: [
        { text: "Valid", value: "V" },
        { text: "Expired", value: "E" },
        { text: "Suspended", value: "S" },
        { text: "Withdrawn", value: "W" },
        { text: "Issued", value: "I" },
      ],
      filterFn: (row, id, filterValue) => {
        const val = row.getValue(id);
        if (!filterValue) return true;
        if (val === undefined || val === null) return false;
        return String(val).toLowerCase() === String(filterValue).toLowerCase();
      },
      Cell: ({ cell }: any) => {
        const statusMapping: Record<string, string> = {
          V: "Valid",
          E: "Expired",
          S: "Suspended",
          W: "Withdrawn",
          I: "Issued",
        };

        // Extract the status value from the row data
        const statusValue = cell.getValue();

        // Map the status value to the corresponding label
        const label = statusMapping[statusValue] || statusValue;

        return <span>{label}</span>;
      }
    },
    {
      accessorKey: "scCreationDate",
      header: "SC Creation Date",
      minSize: 60,
      maxSize: 1000,
      filterVariant: "date",
      Cell: ({ cell }: { cell: any }) => {
        const date = cell.getValue(); // Access the raw date value
        const formattedDate = moment(date).isValid()
          ? moment(date).format("YYYY-MM-DD") // Format valid date
          : date; // Fallback to raw value if invalid
        return <span>{formattedDate}</span>;
      }
    },
    {
      accessorKey: "scValidUntil",
      header: "SC Valid Until",
      minSize: 60,
      maxSize: 1000,
      filterVariant: "date",
    },
    {
      id: "brandName",
      accessorFn: (row) => Array.isArray(row.brandName) ? row.brandName.join(", ") : (row.brandName || ""),
      header: "Brand Name",
      minSize: 60,
      maxSize: 1000,
      filterFn: (row, id, filterValue) => {
        const val = row.getValue(id);
        if (!filterValue) return true;
        return String(val).toLowerCase().includes(String(filterValue).toLowerCase());
      },
      Cell: ({ cell }: any) => {
        const selectedValues = cell.getValue();
        return (
          <div>
            {selectedValues}
          </div>
        );
      },
    },

    {
      accessorKey: "submissionStatus",
      header: "Submission Status",
      minSize: 60,
      maxSize: 1000,
      filterVariant: "select",
      filterSelectOptions: [
        { text: "Draft", value: "Draft" },
        { text: "Submitted", value: "Submitted" },
        { text: "Rejected", value: "Rejected" },
        { text: "Amending", value: "Amending" },
        { text: "Success", value: "Success" },
      ],
      filterFn: (row, id, filterValue) => {
        const val = row.getValue(id);
        if (!filterValue) return true;
        return String(val).toLowerCase() === String(filterValue).toLowerCase();
      },
      Cell: StatusCell
    },
    {
      accessorKey: "scUploadedFiles",
      header: "Uploaded File(s)",
      minSize: 180,
      maxSize: 1200,
      Cell: ({ cell, row }: { cell: any; row: any }) => {
        const uploadedFiles: Array<{ fileName: string; uploadedAt?: string }> =
          cell.getValue() || [];
        if (!uploadedFiles || uploadedFiles.length === 0) return null;

        const filteredFiles = uploadedFiles.filter((fileEntry: any) => {
          const fileName = fileEntry.fileName || fileEntry || "";
          
          const normalize = (str: string) => (str || "").toLowerCase().replace(/[-_\s]/g, "");
          const normFileName = normalize(fileName);
          const normScNo = normalize(row.original.scNo);
          const normScVersionNo = normalize(row.original.scVersionNo);

          const matchesVersion = normScVersionNo && normFileName.includes(normScVersionNo);
          const matchesBase = normScNo && normFileName.includes(normScNo);

          // Only keep base certificate files (-00) or files matching the active version
          return matchesBase || matchesVersion;
        });

        if (filteredFiles.length === 0) return null;

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {filteredFiles.map((fileEntry: any, idx: number) => (
              <UploadedFileLink
                key={idx}
                cell={cell}
                row={row}
                fileName={fileEntry.fileName || fileEntry}
              />
            ))}
          </div>
        );
      },
      enableColumnFilter: false,
    },
    {
      accessorKey: "scPdfUploadStatus",
      header: "Upload Status",
      minSize: 100,
      maxSize: 1000,
      filterVariant: "select",
      filterSelectOptions: [
        { text: "Uploaded", value: "Uploaded" },
        { text: "Not Uploaded", value: "Not Uploaded" },
      ],
      filterFn: (row, id, filterValue) => {
        const val = row.getValue(id);
        if (!filterValue) return true;
        if (val === undefined || val === null) return false;
        return String(val).toLowerCase() === String(filterValue).toLowerCase();
      },
      Cell: ({ cell, row }: { cell: any; row: any }) => {
        const status = cell.getValue() as string;
        const submissionStatus = row.original.submissionStatus;

        if (!status) {
          // Only show 'Not Uploaded' pill for successfully submitted or amended SCs
          return ["Success", "Amending"].includes(submissionStatus) ? (
            <Chip label="Not Uploaded" color="default" size="small" variant="outlined" />
          ) : null;
        }

        const color = status === "Uploaded" ? "success" : "default";
        return <Chip label={status} color={color} size="small" variant="outlined" />;
      }
    },
    {
      accessorKey: "rejectionReason",
      header: "Reject Reason",
      minSize: 80,
      maxSize: 1000,
      Cell: ({ row }: any) => <RejectReasonCell row={row} />,
      enableColumnFilter: false,
    }
  ], [standardDetails]);

  const historicalColumns = useMemo<MRT_ColumnDef<any>[]>(() => [
    {
      accessorKey: "sc_no",
      header: "SC No.",
      minSize: 100,
      maxSize: 1000,
    },
    {
      accessorKey: "version_number",
      header: "SC Version Number",
      minSize: 120,
      maxSize: 1000,
    },
    {
      accessorKey: "co_name",
      header: "Certified Organization Name",
      minSize: 150,
      maxSize: 1000,
    },
    {
      id: "sc_standard",
      accessorFn: (row) => Array.isArray(row.sc_standard) ? row.sc_standard.join(", ") : (row.sc_standard || ""),
      header: "SC Standard",
      minSize: 150,
      maxSize: 1000,
      filterVariant: "select",
      filterSelectOptions: standardDetails.map(det => ({ text: det.label, value: det.value })),
      filterFn: (row, id, filterValue) => {
        const val = row.getValue(id);
        if (!filterValue) return true;
        return String(val).toLowerCase().includes(String(filterValue).toLowerCase());
      },
      Cell: ({ cell }: any) => {
        const value = cell.getValue();
        const selectedValues = typeof value === 'string' ? value.split(", ") : (Array.isArray(value) ? value : []);
        if (!selectedValues || selectedValues.length === 0 || (selectedValues.length === 1 && !selectedValues[0])) {
          return null;
        }
        return (
          <Box display="flex" flexWrap="wrap" gap="4px">
            {selectedValues.map((val: any) => {
              const { code, label } = getStandardDisplayInfo(val);
              if (!code) return null;
              return (
                <Tooltip title={label} arrow key={code}>
                  <Chip
                    label={code}
                    size="small"
                    sx={{
                      backgroundColor: 'rgba(44, 71, 72, 0.08)',
                      color: '#2C4748',
                      fontWeight: 'bold',
                      border: '1px solid rgba(44, 71, 72, 0.2)',
                      borderRadius: '6px',
                      fontSize: '11px',
                      height: '22px',
                      textTransform: 'uppercase',
                      cursor: 'help',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: '#2C4748',
                        color: '#FFFFFF',
                        borderColor: '#2C4748',
                      }
                    }}
                  />
                </Tooltip>
              );
            })}
          </Box>
        );
      },

    },
    {
      accessorKey: "sc_status",
      header: "SC Status",
      minSize: 80,
      maxSize: 1000,
      filterVariant: "select",
      filterSelectOptions: [
        { text: "Valid", value: "V" },
        { text: "Expired", value: "E" },
        { text: "Suspended", value: "S" },
        { text: "Withdrawn", value: "W" },
      ],
      filterFn: (row, id, filterValue) => {
        const val = row.getValue(id);
        if (!filterValue) return true;
        if (val === undefined || val === null) return false;
        return String(val).toLowerCase() === String(filterValue).toLowerCase();
      },
      Cell: ({ cell }: any) => {
        const statusMapping: Record<string, string> = {
          V: "Valid",
          E: "Expired",
          S: "Suspended",
          W: "Withdrawn",
        };

        // Extract the status value from the row data
        const statusValue = cell.getValue();

        // Map the status value to the corresponding label
        const label = statusMapping[statusValue] || statusValue;

        return <span>{label}</span>;
      }
    },
    {
      accessorKey: "creation_date",
      header: "SC Creation Date",
      minSize: 100,
      maxSize: 1000,
      filterVariant: "date",
    },
    {
      accessorKey: "date_of_issue",
      header: "Date of Issue",
      minSize: 100,
      maxSize: 1000,
      filterVariant: "date",
    },
    {
      accessorKey: "sc_valid_until",
      header: "SC Valid Until",
      minSize: 100,
      maxSize: 1000,
      filterVariant: "date",
    },
    {
      id: "brand_name",
      accessorFn: (row) => Array.isArray(row.brand_name) ? row.brand_name.join(", ") : (row.brand_name || ""),
      header: "Brand Name",
      minSize: 100,
      maxSize: 1000,
      filterFn: (row, id, filterValue) => {
        const val = row.getValue(id);
        if (!filterValue) return true;
        return String(val).toLowerCase().includes(String(filterValue).toLowerCase());
      },
    },
  ], [standardDetails]);
  const handleCertifiedOrganizationNameChange = (value: any) => {
    formRef.current.setFieldValue("coName", value == null ? "" : value.value);
  };
  const fetchTEDetallsFromAPI = async (
    value: string,
    offset: number,
    type: string,
  ): Promise<any[]> => {
    try {
      const queryParams: Record<string, any> = {
        name: type == "query" ? value : "",
        offset: offset,
        limit: 10,
        scCbLicensingCode: cbLicensingCode
      };
      // Fetch TE Details using the getTEDetails function
      const response = singleValue === "dTrackit" ? await coNameDetailsService.getdtrackitCoNameDetails(queryParams)
        : await coNameDetailsService.gethistoricCoNameDetails(queryParams);

      const data = response.data.response.map((item: any) => ({
        label: item,
        value: item,
      }));


      return data;
    } catch (error) {
      console.error("Error fetching TE details:", error);
      throw error; // Re-throw error to handle it in the calling code
    }
  };

  const handleSCNOChange = (value: any) => {
    formRef.current.setFieldValue("scNo", value == null ? "" : value.value);
  };

  const getSCNoforSearch = async (
    value: string,
    offset: number,
    type: string,
    name?: string
  ) => {
    try {
      const queryParams: Record<string, any> = {
        scNo: type == "query" ? value : "",
        offset: offset,
        name: type == "name" ? value : "",
        scCbLicensingCode: cbLicensingCode
      };
      const response: any = await executeGet(API_ROUTES.GET_SC_NO_V4, queryParams);
      const data = response.data.response.map((item: any) => ({
        label: item,
        value: item,
      }));
      // //alert(JSON.stringify(response.data))
      return data;
    }
    catch (e) {
      console.error(`Error in Fetching SCNo ${e}`);
    }
  }

  const formConfig = {
    pageName: "sc-landing-page",
    initialValues: {
      coName: "", // For multi-select
      startDate: null,
      endDate: null,
      source: singleValue,
    },
    validationSchema: Yup.object().shape({
      coName: Yup.string().nullable(),
      startDate: Yup.string().nullable(),
      endDate: Yup.string().nullable(),
      source: Yup.string().nullable(),
    }),
    sections: [
      {
        fields: [
          {
            label: "Certified Organization Name",
            name: "coName",
            type: "search",
            gridSize: { xs: 12, md: 3, lg: 3 },
            required: false,
            props: {
              size: "small",
              onChange: handleCertifiedOrganizationNameChange,
              displayKey: "label",
              placeholder: "Search for the certified organization name",
              onSearch: (query: string, offset: number) => {
                const response = fetchTEDetallsFromAPI(query, offset, "query");
                return response;
              },
            },
          },
          // Start Date Field
          {
            label: "Start Date (Created Date)",
            name: "startDate",
            type: "date",
            gridSize: { xs: 12, md: 3, lg: 3 },
            required: false,
            value: null, // Initial value as null for date
            props: {
              placeholder: "YYYY-MM-DD",
            },
          },
          // End Date Field
          {
            label: "End Date (Created Date)",
            name: "endDate",
            type: "date",
            gridSize: { xs: 12, md: 3, lg: 3 },
            required: false,
            value: null, // Initial value as null for date
            props: {
              placeholder: "YYYY-MM-DD",
            },
          },
          {
            label: "Scope Certificate Number",
            name: "scNo",
            type: "search",
            gridSize: { xs: 12, md: 3, lg: 3 },
            required: false,
            props: {
              placeholder: "Search for the scope certificate number",
              onChange: handleSCNOChange,
              displayKey: "label",
              onSearch: (query: string, offset: number) => {
                const response = getSCNoforSearch(query, offset, "query");
                return response;
              },
              fullWidth: true,
            },
          },
          // {
          //   label: "Source",
          //   name: "source",
          //   type: "select",
          //   gridSize: { xs: 12, md: 3, lg: 3 },
          //   required: false,
          //   props: {
          //     size: "small",
          //     options: sourceOptions,
          //     onChange: (event: any) => {
          //       setSingleValue(event.target.value);
          //     },
          //   },
          // },
        ],
      },
    ],
    handleSubmit: async (values: any) => {
      await handleSearchSubmit(values, 1);
    },
  };
  const handleSearchSubmit = async (values: any, isReseted?: number) => {
    try {
      if (isReseted === 1) {
        const isEmpty = (val: any) => StringValidation.isNullOrEmpty(val);
        const allFieldsEmpty = [values.coName, values.scNo, values.startDate, values.endDate].every(isEmpty);
        if (allFieldsEmpty) {
          notify("Please fill at least one field", "error");
          return;
        }
      }

      // Format dates properly
      let params: any = {
        scNo: values.scNo || null,
        startDate: values.startDate ? moment(values.startDate).format('YYYY-MM-DD') : null,
        endDate: values.endDate ? moment(values.endDate).format('YYYY-MM-DD') : null,
      };

      // Handle co_name parameter properly
      if (values.coName) {
        if (Array.isArray(values.coName)) {
          params.co_name = values.coName.filter((name: string) => name && name.trim() !== '');
        } else if (typeof values.coName === 'string' && values.coName.trim() !== '') {
          params.co_name = [values.coName.trim()];
        }
      }

      // Remove null/undefined values
      Object.keys(params).forEach(key => {
        if (params[key] === null || params[key] === undefined ||
          (Array.isArray(params[key]) && params[key].length === 0)) {
          delete params[key];
        }
      });

      console.log('Search parameters:', params);

      if (singleValue === 'dTrackit') {
        await fetchDtraickitDetails(params);
      } else {
        await fetchHistoricalDetails(params);
      }

      if (isReseted === 1) {
        setResetEnable(true);
      } else {
        setResetEnable(false);
      }

    } catch (error) {
      console.log("Error fetching data: ", error);
      notify("Error occurred while searching. Please try again.", "error");
    }
  };
  const handleReset = async () => {
    formRef.current.resetForm();
    setResetEnable(false);

    // Reset pagination to first page
    setPagination({ pageIndex: 0, pageSize: 25 });

    // Clear all data
    setDTrackitdata([]);
    setHistoricdata([]);
    setRowCount(0);

    // Fetch fresh data without any filters
    if (singleValue === 'dTrackit') {
      await fetchDtraickitDetails();
    } else {
      await fetchHistoricalDetails();
    }
  };

  // useEffect(() => {
  //   const values = formRef.current?.values;
  //   if (!values) return true;

  //   const { coName, startDate, endDate } = values;

  //   if (
  //     !coName || coName === "" ||
  //     !startDate ||
  //     !endDate
  //   ){

  //   }

  // },[formRef.current?.values])



  const handleSubmit = () => {
    if (formRef.current) {
      formRef.current.submitForm();
    }
  };

  return (
    <>
      <PageWrapper>
        {/* Compressed Split-Header Row */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          width="100%"
          mb="20px"
        >
          <Typography
            variant="h5"
            fontWeight="600"
            sx={{ color: '#2C4748', letterSpacing: '-0.5px' }}
          >
            Scope Certificates - Version 4.0
          </Typography>
          <StyledButton
            onClick={onNewRequestTCClick}
            variant="contained"
            sx={{
              height: "40px",
              backgroundColor: '#F0CD7F',
              color: '#1C1C1C',
              '&:hover': {
                backgroundColor: '#E0B86F',
              }
            }}
          >
            Create New Scope Certificate
          </StyledButton>
        </Box>

        {/* Integrated Premium Filter Card */}
        <Paper
          elevation={0}
          sx={{
            border: '1px solid rgba(44, 71, 72, 0.12)',
            borderRadius: '12px',
            backgroundColor: '#FFFFFF',
            overflow: 'hidden',
            mb: 3,
          }}
        >
          {/* Inner padding for the filter fields */}
          <Box sx={{ p: 2.5, pb: 1.5 }}>
            <FormBuilderV4
              formConfig={formConfig}
              onSubmit={formConfig.handleSubmit}
              formRef={formRef}
            />
          </Box>

          {/* Styled footer action toolbar */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              px: 2.5,
              py: 1.5,
              backgroundColor: '#F8FAF9',
              borderTop: '1px solid rgba(44, 71, 72, 0.08)',
            }}
          >
            {/* Left side: column filter instruction tip */}
            <Box display="flex" alignItems="center" gap={1}>
              <FilterListIcon
                sx={{
                  color: '#2C4748',
                  fontSize: '18px',
                  opacity: 0.8,
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: '#6A7879',
                  fontWeight: 500,
                }}
              >
                You can use column filters in the table below to filter loaded data on the current page.
              </Typography>
            </Box>

            {/* Right side: Search & Reset buttons */}
            <Box display="flex" alignItems="center" gap={1.5}>
              {isResetEnabled && (
                <StyledButton
                  onClick={handleReset}
                  variant="outlined"
                  sx={{
                    height: "36px",
                    backgroundColor: 'transparent',
                    border: '1px solid rgba(44, 71, 72, 0.3)',
                    color: '#2C4748',
                    marginRight: 0, // Override default margin
                    '&:hover': {
                      backgroundColor: 'rgba(44, 71, 72, 0.05)',
                      border: '1px solid #2C4748',
                    }
                  }}
                >
                  Reset
                </StyledButton>
              )}
              <StyledButton
                onClick={handleSubmit}
                variant="contained"
                sx={{
                  height: "36px",
                  backgroundColor: '#F0CD7F',
                  color: '#1C1C1C',
                  marginRight: 0, // Override default margin
                  '&:hover': {
                    backgroundColor: '#E0B86F',
                  }
                }}
              >
                Search
              </StyledButton>
            </Box>
          </Box>
        </Paper>

        <CustomTable
          columns={singleValue === "dTrackit" ? dtrackitcolumns : historicalColumns}
          data={singleValue === "dTrackit" ? dTrackitdata : historicdata}
          title="TransactionData"
          enableTopToolbar={true}
          enableBottomToolbar={true}
          isLoadingTodos={loading}
          rowCount={rowCount}
          paginations={pagination} // Pass current pagination state
          setPaginations={setPagination}
          enableStickyHeader={true}
          maxHeight="calc(100vh - 290px)"
        />
      </PageWrapper>
    </>
  );
};

export default SuppliersDashboard;
